const CHUNK_RELOAD_STORAGE_KEY = 'studio:chunk-reload'
const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
]

function readRetryState(storage) {
  if (!storage?.getItem) {
    return null
  }

  try {
    const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    return typeof parsed?.at === 'number' ? parsed : null
  } catch {
    return null
  }
}

function writeRetryState(storage, location, now) {
  if (!storage?.setItem) {
    return
  }

  try {
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, JSON.stringify({
      at: now,
      href: location?.href || '',
    }))
  } catch {
    // Ignore storage failures and still attempt a reload.
  }
}

function extractErrorMessage(value) {
  if (!value) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value?.message === 'string') {
    return value.message
  }
  if (typeof value?.reason?.message === 'string') {
    return value.reason.message
  }
  if (typeof value?.error?.message === 'string') {
    return value.error.message
  }
  if (typeof value?.payload?.message === 'string') {
    return value.payload.message
  }
  return ''
}

export function isDynamicImportFailure(value) {
  const message = extractErrorMessage(value)
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export function attemptChunkReload({ location = window.location, storage = window.sessionStorage, now = Date.now() } = {}) {
  const previous = readRetryState(storage)
  if (previous && now - previous.at < CHUNK_RELOAD_COOLDOWN_MS) {
    return false
  }

  writeRetryState(storage, location, now)

  if (typeof location?.reload === 'function') {
    location.reload()
    return true
  }

  if (typeof location?.assign === 'function' && location?.href) {
    location.assign(location.href)
    return true
  }

  return false
}

export function installChunkReloadHandler({ win = window, location = window.location, storage = window.sessionStorage } = {}) {
  if (!win?.addEventListener) {
    return () => {}
  }

  const maybeReload = (value, event) => {
    if (!isDynamicImportFailure(value)) {
      return false
    }

    event?.preventDefault?.()
    return attemptChunkReload({ location, storage })
  }

  const onError = (event) => {
    maybeReload(event?.error || event?.message || event, event)
  }

  const onUnhandledRejection = (event) => {
    maybeReload(event?.reason || event, event)
  }

  const onVitePreloadError = (event) => {
    maybeReload(event?.payload || event, event)
  }

  win.addEventListener('error', onError)
  win.addEventListener('unhandledrejection', onUnhandledRejection)
  win.addEventListener('vite:preloadError', onVitePreloadError)

  return () => {
    win.removeEventListener?.('error', onError)
    win.removeEventListener?.('unhandledrejection', onUnhandledRejection)
    win.removeEventListener?.('vite:preloadError', onVitePreloadError)
  }
}

